import { Property } from '../../property';
import { DimensionAssignment, DimensionModel } from '../models/dimension-assignment';
import { Model as PropertySetModel } from '../models/model';
import { PropertyAssignment } from '../models/property-assignment';
import { Submission } from '../models/submission';

// One automotive and one non-automotive asset type, sharing only model year.
// A test that assumes vehicle fields fails on the vessel set. That's the point.

const FIXED_DATE = new Date('2026-01-01T00:00:00.000Z');

function textProperty(id: string, name: string, valueKey: string): Property.Model<Property.PropertyType.TEXT> {
  return {
    id,
    name,
    valueKey,
    type: Property.PropertyType.TEXT,
    formId: null,
    config: { format: 'text' },
    created: FIXED_DATE,
    modified: FIXED_DATE,
  } as Property.Model<Property.PropertyType.TEXT>;
}

function numberProperty(id: string, name: string, valueKey: string): Property.Model<Property.PropertyType.NUMBER> {
  return {
    id,
    name,
    valueKey,
    type: Property.PropertyType.NUMBER,
    formId: null,
    config: { format: 'number' },
    created: FIXED_DATE,
    modified: FIXED_DATE,
  } as Property.Model<Property.PropertyType.NUMBER>;
}

function assign(
  setId: string,
  property: Property.Model<Property.PropertyType>,
  order: number,
  options: { required?: boolean; hidden?: boolean } = {}
): PropertyAssignment {
  return {
    propertyId: property.id,
    property,
    setId,
    order,
    required: options.required ?? true,
    hidden: options.hidden ?? false,
  };
}

function assignDimension(
  setId: string,
  dimension: DimensionModel,
  order: number,
  options: { required?: boolean; hidden?: boolean } = {}
): DimensionAssignment {
  return {
    dimensionId: dimension.id,
    dimension,
    setId,
    order,
    required: options.required ?? true,
    hidden: options.hidden ?? false,
  };
}

export interface VscAssetFixture {
  /** The set as the API returns it. */
  propertySet: PropertySetModel;

  /** Matches ProvisionWarrantyCommand's two value bags. */
  propertyValues: Record<string, unknown>;
  dimensionValues: Record<string, unknown>;

  /** Defined only here — the sibling must never show them. */
  exclusiveValueKeys: string[];
  exclusiveDimensionIds: string[];
}

// The one field both sets share, so the pair stays comparable.
const modelYear = numberProperty('prop_model_year', 'Model Year', 'modelYear');

const AUTOMOTIVE_SET_ID = 'pset_vsc_road_vehicle';
const NON_AUTOMOTIVE_SET_ID = 'pset_vsc_marine_vessel';

const odometer: DimensionModel = { id: 'dim_vsc_odometer', title: 'Odometer Reading', isScalar: true };
const engineHours: DimensionModel = { id: 'dim_vsc_engine_hours', title: 'Engine Hours', isScalar: true };

export const AUTOMOTIVE_VSC_FIXTURE: VscAssetFixture = {
  propertySet: {
    id: AUTOMOTIVE_SET_ID,
    name: 'VSC Road Vehicle',
    icon: 'car-01',
    created: FIXED_DATE,
    modified: FIXED_DATE,
    properties: [
      assign(AUTOMOTIVE_SET_ID, textProperty('prop_vin', 'VIN', 'vin'), 0),
      assign(AUTOMOTIVE_SET_ID, textProperty('prop_make', 'Make', 'make'), 1),
      assign(AUTOMOTIVE_SET_ID, modelYear, 2),
    ],
    dimensions: [assignDimension(AUTOMOTIVE_SET_ID, odometer, 0)],
  },
  propertyValues: { vin: '1HGCM82633A123456', make: 'Honda', modelYear: 2019 },
  dimensionValues: { [odometer.id]: 84210 },
  exclusiveValueKeys: ['vin', 'make'],
  exclusiveDimensionIds: [odometer.id],
};

export const NON_AUTOMOTIVE_VSC_FIXTURE: VscAssetFixture = {
  propertySet: {
    id: NON_AUTOMOTIVE_SET_ID,
    name: 'VSC Marine Vessel',
    icon: 'anchor',
    created: FIXED_DATE,
    modified: FIXED_DATE,
    properties: [
      assign(NON_AUTOMOTIVE_SET_ID, textProperty('prop_hull_id', 'Hull ID', 'hullId'), 0),
      assign(NON_AUTOMOTIVE_SET_ID, numberProperty('prop_length_ft', 'Length (ft)', 'lengthFt'), 1),
      assign(NON_AUTOMOTIVE_SET_ID, modelYear, 2),
      // Both claim surfaces pass showHidden=true, so this covers the off case.
      assign(NON_AUTOMOTIVE_SET_ID, textProperty('prop_hull_material', 'Hull Material', 'hullMaterial'), 3, {
        required: false,
        hidden: true,
      }),
    ],
    dimensions: [assignDimension(NON_AUTOMOTIVE_SET_ID, engineHours, 0)],
  },
  propertyValues: { hullId: 'XYZ12345D404', lengthFt: 32, modelYear: 2019, hullMaterial: 'Fiberglass' },
  dimensionValues: { [engineHours.id]: 450 },
  exclusiveValueKeys: ['hullId', 'lengthFt', 'hullMaterial'],
  exclusiveDimensionIds: [engineHours.id],
};

/** For describe.each — an asset-type branch turns one of them red. */
export const VSC_ASSET_FIXTURES: [string, VscAssetFixture][] = [
  ['automotive', AUTOMOTIVE_VSC_FIXTURE],
  ['non-automotive', NON_AUTOMOTIVE_VSC_FIXTURE],
];

/** What the warranty should hold once provisioned. */
export function submissionFor(fixture: VscAssetFixture, id = 'psub_fixture'): Submission {
  return {
    id,
    setId: fixture.propertySet.id,
    set: fixture.propertySet,
    timestamp: FIXED_DATE,
    value: fixture.propertyValues,
    dimensionValues: fixture.dimensionValues,
  };
}
